import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import Icon from "@/components/Icon";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/lib/motionSafe";

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const activeCategory = searchParams.get("category") || "";
  const searchQ = searchParams.get("q") || "";

  useEffect(() => {
    endpoints.categories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (searchQ) params.search = searchQ;
    endpoints
      .products(params)
      .then((data) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCategory, searchQ]);

  const setCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set("category", slug);
    else next.delete("category");
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    const t = searchInput.trim();
    if (t) next.set("q", t);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const clearSearch = () => {
    setSearchInput("");
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const activeCategoryTitle = useMemo(() => {
    if (!activeCategory) return null;
    const c = categories.find((x) => x.slug === activeCategory);
    return c?.title_ar;
  }, [activeCategory, categories]);

  return (
    <div data-testid="page-products">
      {/* Hero */}
      <section className="aman-hero relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        <div className="aman-glow-orb -top-20 -end-20" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center text-white relative">
          <Reveal>
            <span className="aman-tag mx-auto">كتالوج المنتجات</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              معدات أمنية بجودة عالية
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">
              تصفّح مجموعتنا من الكاميرات، أنظمة الإنذار، معدات الحريق، وأجهزة الشبكات.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 bg-white sticky top-16 md:top-20 z-30 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Category chips */}
          <div className="flex flex-wrap gap-2 flex-1" data-testid="category-filters">
            <button
              onClick={() => setCategory("")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                !activeCategory
                  ? "bg-[#0A1628] text-white border-[#0A1628]"
                  : "bg-white text-[#0A1628] border-slate-200 hover:border-[#00E5FF]"
              }`}
              data-testid="cat-all"
            >
              جميع المنتجات
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border inline-flex items-center gap-2 ${
                  activeCategory === c.slug
                    ? "bg-[#0A1628] text-white border-[#0A1628]"
                    : "bg-white text-[#0A1628] border-slate-200 hover:border-[#00E5FF]"
                }`}
                data-testid={`cat-${c.slug}`}
              >
                <Icon name={c.icon} size={14} />
                {c.title_ar}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={submitSearch} className="relative w-full lg:w-80" data-testid="products-search-form">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="ابحث عن منتج..."
              className="w-full rounded-full border border-slate-200 bg-white ps-4 pe-11 py-2.5 text-[#0A1628] placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100 transition-colors text-sm"
              data-testid="products-search-input"
            />
            <button
              type="submit"
              className="absolute inset-y-0 end-0 flex items-center pe-3 text-[#0A1628] hover:text-[#00b8cc] transition-colors"
              aria-label="بحث"
              data-testid="products-search-submit"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" strokeLinecap="round" /></svg>
            </button>
          </form>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* result meta */}
          <div className="flex items-center justify-between mb-6" data-testid="products-result-meta">
            <div className="text-slate-600 text-sm">
              {loading ? "جاري التحميل..." : (
                <>
                  <span className="font-bold text-[#0A1628]">{products.length}</span> منتج
                  {activeCategoryTitle && <span className="text-slate-500"> — {activeCategoryTitle}</span>}
                  {searchQ && (
                    <span className="text-slate-500">
                      {" "}— نتائج البحث عن &quot;<span className="text-[#0A1628] font-semibold">{searchQ}</span>&quot;
                      <button onClick={clearSearch} className="ms-2 text-[#00b8cc] hover:underline">مسح</button>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse aman-card overflow-hidden">
                  <div className="aspect-[4/3] bg-slate-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-slate-100 rounded w-1/3" />
                    <div className="h-5 bg-slate-100 rounded w-4/5" />
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto" data-testid="products-empty">
              <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-5">
                <Icon name="boxes" size={30} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#0A1628]">لا توجد نتائج مطابقة</h3>
              <p className="mt-2 text-slate-600">جرّب تعديل كلمة البحث أو اختيار فئة أخرى.</p>
              <button onClick={() => { clearSearch(); setCategory(""); }} className="mt-6 aman-btn-light">
                عرض جميع المنتجات
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} delayMs={i * 60} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
